import { NewActionMenu } from "@/features/actions";
import { BriefingSlot, useCurrentBriefWindow } from "@/features/briefing";
import { useChatStore } from "@/features/chat";
import { validateWorkSearch } from "@/features/workspace-router";
import {
  NudgeClusterRow,
  groupNudgesByTopic,
} from "@/features/workspace/components/NudgeClusterRow";
import { SubTabBar, useActiveSubTab } from "@/features/workspace/components/SubTabBar";
import { TaskComposer } from "@/features/workspace/components/TaskComposer";
import { TaskSimpleRow } from "@/features/workspace/components/TaskSimpleRow";
import { WorkSearchFilters } from "@/features/workspace/components/searchFilters";
import type { SubTabDef } from "@/features/workspace/config";
import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import {
  NUDGE_INBOX_QUERY_KEY,
  NUDGE_UNREAD_QUERY_KEY,
  useNudgeInbox,
} from "@/features/workspace/hooks/useNudgeInbox";
import { usePendingActions } from "@/features/workspace/hooks/usePendingActions";
import { useSearchNavigation } from "@/features/workspace/hooks/useSearchNavigation";
import {
  todayTasksQueryOptions,
  useCompleteTask,
  usePatchTask,
  useTodayTasks,
  useUncompleteTask,
} from "@/features/workspace/hooks/useTodayTasks";
import { ackNudge, dismissNudge } from "@ando/core";
import { toast } from "@ando/ui";
import {
  Button,
  ListItemEyebrow,
  ListItemRow,
  SurfaceCommandBar,
  SurfaceEmptyState,
  SurfaceFiltersPopover,
  SurfaceLayout,
  WorkspaceSearchBar,
} from "@ando/ui";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { Bell, Check, CheckSquare, ChevronRight, Plus } from "lucide-react";
import type * as React from "react";
import { useCallback, useMemo, useState } from "react";

export const Route = createFileRoute("/_authed/workspace/work")({
  validateSearch: validateWorkSearch,
  // Warm the "Da fare oggi" task feed on hover/focus so the hub paints from
  // cache instead of spinning on mount.
  loader: ({ context: { queryClient } }) => {
    void queryClient.ensureQueryData(todayTasksQueryOptions());
  },
  component: WorkHub,
});

/**
 * Work hub — three coexisting sections (mockup v18 r3411-3483):
 *
 *   1. "Suggeriti da Ando"  — HITL action proposals
 *                             (``GET /actions/pending`` → ``usePendingActions``).
 *   2. "Da fare oggi"       — pending tasks due in the next 24h
 *                             (``GET /tasks/today`` → ``useTodayTasks``).
 *   3. "Completate · oggi"  — tasks marked done since the UTC day-start.
 *
 * The two task sections share a single roundtrip (``/tasks/today``) so the
 * companion-hero counts stay in sync with the rendered rows. Mutations
 * (complete / uncomplete) invalidate the same query key.
 *
 * BE source of truth:
 *   - ``ando-agent/src/api/v1/endpoints/actions.py`` (pending proposals)
 *   - ``ando-agent/src/api/v1/endpoints/tasks.py``   (today feed + mutations)
 */
/**
 * Work hub tabs (Jul 2026 refactor). The old surface stacked FOUR sections
 * (proposals + Oggi + Prossimi + Fatte) into one flat "listone" that scrolled
 * as an unreadable wall, while the sub-tabs only filtered the tiny proposals
 * list — a confusing double system. Now each tab renders EXACTLY ONE dense
 * list, so you see one bucket at a time (SOTA Jul 2026: one focused list +
 * compact single-line rows + collapsible chunks beats a flat mega-list).
 *
 *   - ``today``    — deadline nudges + tasks due now (default).
 *   - ``upcoming`` — future tasks, chunked into collapsible day groups.
 *   - ``done``     — completed today.
 *   - ``ando``     — HITL proposals Ando suggests.
 */
const WORK_TABS = ["today", "upcoming", "done", "ando"] as const;
type WorkTab = (typeof WORK_TABS)[number];

function normalizeWorkTab(raw: string): WorkTab {
  return (WORK_TABS as readonly string[]).includes(raw) ? (raw as WorkTab) : "today";
}

/**
 * Split upcoming tasks into day buckets so a long list breaks into
 * collapsible groups (Domani / Questa settimana / Più avanti) instead of
 * one endless column. Tasks with no due date fall into "later".
 */
function bucketUpcoming<T extends { due_at: string | null }>(
  tasks: readonly T[],
  now: number,
): { tomorrow: readonly T[]; week: readonly T[]; later: readonly T[] } {
  const d = new Date(now);
  const startTomorrow = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
  const startDayAfter = startTomorrow + 24 * 3600_000;
  const endWeek = startTomorrow + 7 * 24 * 3600_000;
  const tomorrow: T[] = [];
  const week: T[] = [];
  const later: T[] = [];
  for (const t of tasks) {
    const due = t.due_at ? Date.parse(t.due_at) : Number.NaN;
    if (Number.isFinite(due) && due >= startTomorrow && due < startDayAfter) tomorrow.push(t);
    else if (Number.isFinite(due) && due >= startDayAfter && due < endWeek) week.push(t);
    else later.push(t);
  }
  return { tomorrow, week, later };
}

/**
 * Collapsible group header — a pencil disclosure (chevron + uppercase label
 * + count) that lets the user fold away a chunk of the list. Typography-first,
 * no fill; the chevron is the only affordance.
 */
const CollapsibleGroup: React.FC<{
  readonly label: React.ReactNode;
  readonly count: number;
  readonly ariaLabel: string;
  readonly defaultOpen?: boolean;
  readonly children: React.ReactNode;
}> = ({ label, count, ariaLabel, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="group flex w-full items-center gap-1.5 py-1.5 text-left"
      >
        <ChevronRight
          size={13}
          strokeWidth={2}
          aria-hidden
          className={`text-ink-4 transition-transform ${open ? "rotate-90" : ""}`}
        />
        <span className="font-semibold text-[11px] text-ink-3 uppercase tracking-[0.08em]">
          {label}
        </span>
        <span className="text-[10px] text-ink-4 tabular-nums">{count}</span>
      </button>
      {open ? (
        <ul className="flex flex-col" aria-label={ariaLabel}>
          {children}
        </ul>
      ) : null}
    </div>
  );
};

/**
 * AI/chat fallback — the SECONDARY "Compila con AI" path on the inline
 * :class:`TaskComposer`. Seeds the chat composer with a "Crea task:" prompt
 * so a user can describe the task in natural language; Ando then proposes a
 * task via the action-card flow. The PRIMARY path is now the inline form
 * (``POST /tasks`` straight from :class:`TaskComposer`) — no chat detour.
 */
const NEW_TASK_DRAFT_PROMPT: MessageDescriptor = msg`Crea task: `;

function WorkHub() {
  const { i18n } = useLingui();
  const search = useSearch({ from: "/_authed/workspace/work" });
  const searchNav = useSearchNavigation<{
    status: ReadonlyArray<string>;
    priority: ReadonlyArray<string>;
    period: string;
  }>("/workspace/work");
  const { actions, isLoading: actionsLoading, isError: actionsError } = usePendingActions(20);
  const activeSubTab = useActiveSubTab("work");
  const selectConversation = useChatStore((s) => s.selectConversation);
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);
  // ``setBriefingHasContent`` still feeds ``BriefingSlot onLoaded``; the value
  // itself is no longer read (its only consumer was the CompanionHero, removed
  // with the Jul 2026 command-bar de-layering).
  const [, setBriefingHasContent] = useState(false);
  // Pick ``morning`` / ``day`` based on the user's local clock so the
  // greeting + lookback match reality (default was hardcoded "morning"
  // → "Buongiorno" + "MATTINA" badge at 20:00). See ``currentBriefWindow``.
  const briefWindow = useCurrentBriefWindow();

  const tasksQuery = useTodayTasks();
  const completeTask = useCompleteTask();
  const uncompleteTask = useUncompleteTask();
  const patchTask = usePatchTask();

  // Snooze + dismiss handlers — ``useCallback`` keeps the same function
  // identity across renders so a future ``React.memo(TaskSimpleRow)``
  // doesn't re-render every row when this hub re-renders for an
  // unrelated reason. Dep is the mutation's stable ``mutate`` ref.
  const patchMutate = patchTask.mutate;
  const handleSnooze = useCallback(
    (taskId: string, offsetHours: number): void => {
      const due_at = new Date(Date.now() + offsetHours * 3_600_000).toISOString();
      patchMutate({ taskId, patch: { due_at } });
    },
    [patchMutate],
  );
  const handleDismiss = useCallback(
    (taskId: string): void => {
      patchMutate({ taskId, patch: { is_deleted: true } });
    },
    [patchMutate],
  );

  // Notifiche di Ando — same inbox the bell popover shows. Shared
  // ``useNudgeInbox`` hook so cache + WS invalidation are unified across
  // surfaces (May 31, 2026). Limit 20 here: the work hub is the
  // "everything I should look at" surface, the bell stays at 8.
  const nudgesQuery = useNudgeInbox({ limit: 20 });
  // Memoise the cluster grouping — recomputed only when the inbox
  // items array reference changes (TanStack returns a new ref on
  // refetch). Without ``useMemo`` the topic_key grouping ran on every
  // unrelated parent re-render (Jun 10, 2026 review).
  const nudgeClusters = useMemo(
    () => groupNudgesByTopic(nudgesQuery.data?.items ?? []),
    [nudgesQuery.data?.items],
  );
  const queryClient = useQueryClient();
  const nudgeAck = useMutation({
    mutationFn: (id: string) => ackNudge(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NUDGE_INBOX_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NUDGE_UNREAD_QUERY_KEY });
    },
    onError: (err) => toast.error(i18n._(msg`Impossibile leggere: ${err.message}`)),
  });
  const nudgeDismiss = useMutation({
    mutationFn: (id: string) => dismissNudge(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NUDGE_INBOX_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NUDGE_UNREAD_QUERY_KEY });
    },
    onError: (err) => toast.error(i18n._(msg`Impossibile ignorare: ${err.message}`)),
  });

  const tasksData = tasksQuery.data;
  const pendingTasks = tasksData?.pending ?? [];
  const upcomingTasks = tasksData?.upcoming ?? [];
  const completedTasks = tasksData?.completed_today ?? [];

  // Proposals are their own tab ("Da Ando") now — no per-sub-tab slicing.
  const visibleActions = actions;
  const tab = normalizeWorkTab(activeSubTab);

  // Inline form is the PRIMARY create path (no chat detour).
  const [taskComposerOpen, setTaskComposerOpen] = useState(false);
  const handleNewTask = () => setTaskComposerOpen(true);
  // Secondary "Compila con AI" affordance — seeds the chat composer.
  const handleNewTaskViaChat = () => {
    selectConversation(null);
    setComposerDraft(i18n._(NEW_TASK_DRAFT_PROMPT));
  };

  // One tab = one dense list. Badges count what each tab actually shows,
  // so the numbers match the rendered rows (the old badges counted a
  // sub-slice of proposals that never appeared under those labels).
  const chips = useMemo<readonly SubTabDef[]>(
    () => [
      {
        id: "today",
        label: i18n._(msg`Oggi`),
        badge: nudgeClusters.length + pendingTasks.length,
      },
      {
        id: "upcoming",
        label: i18n._(msg`Prossimi`),
        badge: upcomingTasks.length,
      },
      {
        id: "done",
        label: i18n._(msg`Fatte`),
        badge: completedTasks.length,
      },
      {
        id: "ando",
        label: i18n._(msg`Da Ando`),
        badge: actions.length,
      },
    ],
    [
      nudgeClusters.length,
      pendingTasks.length,
      upcomingTasks.length,
      completedTasks.length,
      actions.length,
      i18n,
    ],
  );

  const totalActions = visibleActions.length;
  const totalPending = pendingTasks.length;
  const tasksLoading = tasksQuery.isLoading;

  return (
    <SurfaceLayout
      header={
        // Jul 2026 — single-row command bar (replaces stacked header + hero +
        // search + filter rows). The nav tab names the area; ``count`` carries
        // the summary; filters live behind the shared Filtri popover.
        <SurfaceCommandBar
          count={
            actionsLoading && tasksLoading ? (
              <span className="text-ink-3">
                <Trans>Caricamento…</Trans>
              </span>
            ) : (
              <>
                <strong>
                  <Trans>
                    <span className="tabular-nums">{totalPending}</span> in sospeso
                  </Trans>
                </strong>
                {totalActions > 0 ? (
                  <Trans>
                    {" "}
                    · <span className="tabular-nums">{totalActions}</span> proposte AI
                  </Trans>
                ) : null}
              </>
            )
          }
          search={
            <WorkspaceSearchBar
              placeholder={i18n._(msg`Cerca task, attività…`)}
              ariaLabel={i18n._(msg`Cerca lavoro`)}
              initialQuery={search.q}
              onSearch={searchNav.setQuery}
            />
          }
          views={<SubTabBar tabId="work" chips={chips} className="!px-0 !py-0" />}
          filters={
            <SurfaceFiltersPopover
              ariaLabel={i18n._(msg`Filtri lavoro`)}
              label={<Trans>Filtri</Trans>}
              activeCount={
                (search.status?.length ?? 0) +
                (search.priority?.length ?? 0) +
                ((search.period ?? "all") !== "all" ? 1 : 0)
              }
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <WorkSearchFilters
                  status={search.status ?? []}
                  priority={search.priority ?? []}
                  period={search.period ?? "all"}
                  onChange={searchNav.patchFilters}
                />
              </div>
            </SurfaceFiltersPopover>
          }
          actions={
            <NewActionMenu
              area="work"
              label={i18n._(msg`Nuovo`)}
              extraItems={[
                {
                  id: "work:new_task",
                  label: i18n._(msg`Nuovo task`),
                  icon: CheckSquare,
                  onSelect: handleNewTask,
                },
              ]}
            />
          }
        />
      }
    >
      {/* Briefing slot — proactive "Brief & Deep" card for the tasks
          domain. Silent on BE 404 / empty payload. */}
      {/* Multi-domain briefing — replaces the tasks-only pilot (Jun 10, 2026
          fan-out). The aggregate surface weaves inbox / calendar / finance /
          documents / tasks into a single hero card with AI master headline
          + overview. AI-proposed tasks land in "Da fare oggi" below with the
          Sparkles badge so the user can act on them inline. */}
      <BriefingSlot
        domain="aggregate"
        window={briefWindow}
        className="mb-5"
        onLoaded={setBriefingHasContent}
      />
      {/* Inline "Nuovo task" form — PRIMARY manual-create path (no chat).
          Opened from the unified "Nuovo" menu or the empty-state CTA. */}
      {taskComposerOpen ? (
        <TaskComposer
          className="mb-5"
          onClose={() => setTaskComposerOpen(false)}
          onAiAssist={handleNewTaskViaChat}
        />
      ) : null}
      {/* One tab = one dense list (Jul 2026 refactor). No more 4-section
          stack scrolling as an unreadable wall; the active sub-tab picks
          exactly one focused bucket. Rows are single-line (`compact`). */}

      {/* OGGI — deadline nudges first (pressure), then tasks due now. */}
      {tab === "today" ? (
        nudgeClusters.length > 0 || pendingTasks.length > 0 ? (
          <ul className="flex flex-col" aria-label={i18n._(msg`Oggi`)}>
            {nudgeClusters.map((cluster, idx) => (
              <NudgeClusterRow
                key={cluster.topicKey}
                cluster={cluster}
                index={idx}
                onAckAll={(ids) => {
                  for (const id of ids) nudgeAck.mutate(id);
                }}
                onDismissAll={(ids) => {
                  for (const id of ids) nudgeDismiss.mutate(id);
                }}
                isBusy={nudgeAck.isPending || nudgeDismiss.isPending}
              />
            ))}
            {pendingTasks.map((task, idx) => (
              <TaskSimpleRow
                key={task.task_id}
                task={task}
                index={idx}
                compact
                onToggle={(id) => completeTask.mutate(id)}
                isBusy={completeTask.isPending && completeTask.variables === task.task_id}
                onSnooze={handleSnooze}
                onDismiss={handleDismiss}
              />
            ))}
          </ul>
        ) : tasksLoading ? null : (
          <SurfaceEmptyState
            icon={<CheckSquare size={18} strokeWidth={1.75} />}
            title={i18n._(msg`Tutto sotto controllo`)}
            description={i18n._(msg`Nessuna scadenza o task per oggi.`)}
            action={
              <Button size="sm" variant="secondary" onClick={handleNewTask}>
                <Plus size={14} strokeWidth={2} aria-hidden />
                <Trans>Crea un task</Trans>
              </Button>
            }
          />
        )
      ) : null}

      {/* PROSSIMI — future tasks chunked into collapsible day groups so a
          long list folds instead of scrolling forever. */}
      {tab === "upcoming" ? (
        upcomingTasks.length > 0 ? (
          (() => {
            const b = bucketUpcoming(upcomingTasks, Date.now());
            const rows = (list: typeof upcomingTasks) =>
              list.map((task, idx) => (
                <TaskSimpleRow
                  key={task.task_id}
                  task={task}
                  index={idx}
                  compact
                  onToggle={(id) => completeTask.mutate(id)}
                  isBusy={completeTask.isPending && completeTask.variables === task.task_id}
                  onSnooze={handleSnooze}
                  onDismiss={handleDismiss}
                />
              ));
            return (
              <div className="flex flex-col">
                {b.tomorrow.length > 0 ? (
                  <CollapsibleGroup
                    label={<Trans>Domani</Trans>}
                    count={b.tomorrow.length}
                    ariaLabel={i18n._(msg`Domani`)}
                  >
                    {rows(b.tomorrow)}
                  </CollapsibleGroup>
                ) : null}
                {b.week.length > 0 ? (
                  <CollapsibleGroup
                    label={<Trans>Questa settimana</Trans>}
                    count={b.week.length}
                    ariaLabel={i18n._(msg`Questa settimana`)}
                  >
                    {rows(b.week)}
                  </CollapsibleGroup>
                ) : null}
                {b.later.length > 0 ? (
                  <CollapsibleGroup
                    label={<Trans>Più avanti</Trans>}
                    count={b.later.length}
                    ariaLabel={i18n._(msg`Più avanti`)}
                    defaultOpen={false}
                  >
                    {rows(b.later)}
                  </CollapsibleGroup>
                ) : null}
              </div>
            );
          })()
        ) : tasksLoading ? null : (
          <SurfaceEmptyState
            icon={<CheckSquare size={18} strokeWidth={1.75} />}
            title={i18n._(msg`Niente in programma`)}
            description={i18n._(msg`Nessun task nei prossimi giorni.`)}
          />
        )
      ) : null}

      {/* FATTE — completed today. */}
      {tab === "done" ? (
        completedTasks.length > 0 ? (
          <ul className="flex flex-col" aria-label={i18n._(msg`Completate oggi`)}>
            {completedTasks.map((task, idx) => (
              <TaskSimpleRow
                key={task.task_id}
                task={task}
                index={idx}
                compact
                onToggle={(id) => uncompleteTask.mutate(id)}
                isBusy={uncompleteTask.isPending && uncompleteTask.variables === task.task_id}
              />
            ))}
          </ul>
        ) : tasksLoading ? null : (
          <SurfaceEmptyState
            icon={<Check size={18} strokeWidth={1.75} />}
            title={i18n._(msg`Ancora niente di fatto`)}
            description={i18n._(msg`I task che completi oggi compaiono qui.`)}
          />
        )
      ) : null}

      {/* DA ANDO — HITL proposals Ando suggests (single-line). */}
      {tab === "ando" ? (
        actionsError ? (
          <SurfaceEmptyState
            icon={<CheckSquare size={18} strokeWidth={1.75} />}
            title={i18n._(msg`Impossibile caricare le proposte`)}
            description={i18n._(msg`Verifica la connessione e riprova fra qualche secondo.`)}
          />
        ) : visibleActions.length > 0 ? (
          <ul className="flex flex-col" aria-label={i18n._(msg`Suggeriti da Ando`)}>
            {visibleActions.map((t, idx) => {
              const time = formatRelativeTime(t.created_at);
              const source =
                t.source_label !== undefined
                  ? { label: t.source_label }
                  : actionTypeToSource(t.action_type, i18n);
              return (
                <ListItemRow
                  key={t.id}
                  itemId={t.id}
                  index={idx}
                  avatar={
                    <span
                      aria-hidden
                      className="mt-0.5 h-7 w-7 shrink-0 rounded-md border-2 border-accent bg-ando-orange-soft"
                    />
                  }
                  eyebrow={
                    <>
                      <ListItemEyebrow tone="branded">
                        <Trans>Da {source.label}</Trans>
                      </ListItemEyebrow>
                      {t.risk_level ? (
                        <span className="inline-flex items-center rounded border border-hairline px-1.5 py-0.5 font-medium text-[9px] text-ink-3">
                          {t.risk_level}
                        </span>
                      ) : null}
                    </>
                  }
                  title={t.title || i18n._(msg`Senza titolo`)}
                  trailing={
                    time ? (
                      <span className="text-[10px] text-ink-3 tabular-nums">{time}</span>
                    ) : null
                  }
                  onClick={() => {
                    /* Proposal-open routing handled by future HITL drawer. */
                  }}
                  highlighted
                />
              );
            })}
          </ul>
        ) : actionsLoading ? null : (
          <SurfaceEmptyState
            icon={<Bell size={18} strokeWidth={1.75} />}
            title={i18n._(msg`Nessuna proposta`)}
            description={i18n._(msg`Ando ti suggerirà azioni qui quando trova qualcosa da fare.`)}
          />
        )
      ) : null}
    </SurfaceLayout>
  );
}

/** Action_type → human "From" tag — kept local so the route file owns the
 * vendor → label mapping without circling through a feature module. */
function actionTypeToSource(
  actionType: string,
  i18n: ReturnType<typeof useLingui>["i18n"],
): { readonly label: string } {
  if (actionType.startsWith("send_email") || actionType.startsWith("compose_email"))
    return { label: i18n._(msg`email`) };
  if (actionType.startsWith("create_event") || actionType.startsWith("schedule_meeting"))
    return { label: i18n._(msg`calendario`) };
  if (actionType.startsWith("salesforce") || actionType.includes("crm"))
    return { label: i18n._(msg`CRM`) };
  if (actionType.startsWith("invoice") || actionType.includes("fattura"))
    return { label: i18n._(msg`fatture`) };
  if (actionType.startsWith("document") || actionType.startsWith("create_doc"))
    return { label: i18n._(msg`documenti`) };
  if (actionType.includes("chat")) return { label: i18n._(msg`chat`) };
  return { label: "Ando" };
}

// Single-nudge ``NudgeRow`` removed Jun 10, 2026 — every nudge now flows
// through ``NudgeClusterRow`` (clusters of size 1 render the same chrome
// without the "+N fonti" affordance). Lives in ``components/NudgeClusterRow``
// alongside its grouping helper.
